import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Car, CheckCircle, MessageCircle, ArrowRight, Shield, IndianRupee, Clock, Star } from 'lucide-react';
import useSEO from '../utils/useSEO';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.1, ease: 'easeOut' } }),
};

const CarInsurancePage = () => {
  const navigate = useNavigate();

  useSEO({
    title: 'Car Insurance in India | Vehicle Insurance Plans | AGO Innovations',
    description:
      'Compare the best car insurance plans in India. AGO Innovations helps you find comprehensive and third-party vehicle insurance with fast claim support. Free expert consultation.',
    keywords:
      'car insurance India, vehicle insurance plans, comprehensive car insurance, third party car insurance, car insurance renewal, best car insurance India, motor insurance advisor',
    canonical: 'https://agoinnovations.in/finance/car-insurance',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'FinancialService',
      name: 'Car Insurance Advisory — AGO Innovations',
      provider: { '@type': 'Organization', name: 'AGO Innovations', url: 'https://agoinnovations.in' },
      description: 'Expert car and vehicle insurance advisory. Compare comprehensive and third-party plans.',
      areaServed: 'IN',
      serviceType: 'Motor Insurance Advisory',
    },
  });

  const planTypes = [
    { name: 'Third-Party Insurance', desc: 'Mandatory by law in India. Covers damage or injury caused to a third party. Low premium.', badge: 'Mandatory', color: 'bg-orange-600' },
    { name: 'Comprehensive Insurance', desc: 'Covers third-party liability + own damage. Best protection for your vehicle.', badge: 'Recommended', color: 'bg-blue-700' },
    { name: 'Standalone Own Damage', desc: 'Covers damage to your own vehicle only. Useful if you already have third-party cover.', badge: 'Flexible', color: 'bg-emerald-700' },
    { name: 'Zero Depreciation Add-on', desc: 'Get full claim without depreciation deduction. Best for new cars.', badge: 'Popular Add-on', color: 'bg-purple-700' },
  ];

  const features = [
    { icon: <Car className="w-5 h-5" />, title: 'Cashless Repair', desc: 'Repairs at 5,000+ network garages without paying out of pocket.' },
    { icon: <Shield className="w-5 h-5" />, title: 'Own Damage Cover', desc: 'Protection against accidents, theft, fire, and natural disasters.' },
    { icon: <IndianRupee className="w-5 h-5" />, title: 'No Claim Bonus', desc: 'Up to 50% discount on premium for every claim-free year.' },
    { icon: <Clock className="w-5 h-5" />, title: 'Fast Claim Settlement', desc: '24/7 claim support with quick settlement from top insurers.' },
    { icon: <Star className="w-5 h-5" />, title: 'Roadside Assistance', desc: 'Towing, fuel delivery, and emergency help anywhere in India.' },
    { icon: <CheckCircle className="w-5 h-5" />, title: 'Personal Accident Cover', desc: 'Mandatory ₹15 lakh personal accident cover for the owner-driver.' },
  ];

  const faqs = [
    { q: 'Is car insurance mandatory in India?', a: 'Yes. Third-party car insurance is mandatory under the Motor Vehicles Act. Driving without it can result in a fine of ₹2,000 and/or 3 months imprisonment.' },
    { q: 'What is the difference between third-party and comprehensive car insurance?', a: 'Third-party only covers damage or injury to others. Comprehensive covers both third-party liability and damage to your own vehicle — strongly recommended.' },
    { q: 'How is car insurance premium calculated?', a: 'Key factors include your car\'s IDV (Insured Declared Value), engine cubic capacity, car age, city, and your claim history (No Claim Bonus).' },
    { q: 'What is Zero Depreciation car insurance?', a: 'Zero Dep is an add-on that ensures you receive the full claim amount without any deduction for depreciation on car parts. Best for cars less than 5 years old.' },
    { q: 'Can I renew car insurance online?', a: 'Yes. AGO Innovations can help you compare renewal options online and choose the best plan with maximum NCB benefits.' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-br from-orange-900 via-amber-900 to-orange-800 pt-28 pb-20 px-4 text-white text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400" />
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-orange-500/20 rounded-full blur-3xl" />
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="relative z-10 max-w-4xl mx-auto">
          <span className="inline-block bg-yellow-400/20 border border-yellow-400/40 text-yellow-300 text-sm font-semibold px-5 py-1.5 rounded-full mb-5">🚗 Finance — Car Insurance</span>
          <h1 className="text-4xl md:text-6xl font-black mb-5 leading-tight">Car Insurance Plans<span className="block text-yellow-400">in India</span></h1>
          <p className="text-orange-100 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">Compare comprehensive and third-party vehicle insurance plans. Fast claim settlement, cashless repairs, and expert advisory — all in one place.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }} onClick={() => navigate('/contact')} className="bg-yellow-400 hover:bg-yellow-300 text-orange-900 font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2">Get Free Quote <ArrowRight className="w-5 h-5" /></motion.button>
            <a href="https://wa.me/91XXXXXXXXXX?text=Hi!%20I%20need%20car%20insurance%20advice." target="_blank" rel="noopener noreferrer" className="bg-green-500 hover:bg-green-400 text-white font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2"><MessageCircle className="w-5 h-5" /> WhatsApp Us</a>
          </div>
          <div className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-orange-200">
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Comprehensive Plans</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Fast Claim Support</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Cashless Garages</span>
          </div>
        </motion.div>
      </section>

      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">Types of Car Insurance Plans</h2>
            <p className="text-gray-500 text-lg">Understand your options before choosing coverage.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-6">
            {planTypes.map((plan, i) => (
              <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center text-orange-600"><Car className="w-6 h-6" /></div>
                  <span className={`text-white text-xs font-bold px-3 py-1 rounded-full ${plan.color}`}>{plan.badge}</span>
                </div>
                <h3 className="font-black text-gray-900 text-lg mb-2">{plan.name}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{plan.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl font-black text-gray-900 mb-3">Key Car Insurance Benefits</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="bg-orange-50 rounded-2xl p-5 border border-orange-100">
                <div className="w-11 h-11 bg-orange-600 rounded-xl flex items-center justify-center text-white mb-4">{f.icon}</div>
                <h3 className="font-bold text-gray-900 text-sm mb-1">{f.title}</h3>
                <p className="text-gray-600 text-xs leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl font-black text-gray-900 mb-3">Car Insurance FAQs</h2>
          </motion.div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="bg-white rounded-2xl p-6 border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{faq.q}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-gradient-to-br from-orange-800 to-amber-900 text-white text-center">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-black mb-4">Get the Best Car Insurance Deal</h2>
          <p className="text-orange-200 mb-8">Compare plans and get expert guidance — completely free.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate('/contact')} className="bg-yellow-400 hover:bg-yellow-300 text-orange-900 font-bold px-8 py-4 rounded-full transition-colors">Book Free Consultation</button>
            <button onClick={() => navigate('/finance')} className="bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold px-8 py-4 rounded-full transition-colors">All Finance Services</button>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default CarInsurancePage;
